import { useEffect, useState } from 'react';
import { CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { colors } from '@src/styles';
import { useRunsContext } from '../context/RunsContext';
import { monoFontFamily } from '../utils/format';

const MIN_LENGTH = 3;
const DEBOUNCE_MS = 500;

const isCommittable = (term: string): boolean => term.length === 0 || term.length >= MIN_LENGTH;

export function ContextSearchField() {
  const { contextSearch, setContextSearch, isLoadingRuns } = useRunsContext();
  const [value, setValue] = useState(contextSearch);
  const term = value.trim();
  const isTooShort = term.length > 0 && term.length < MIN_LENGTH;
  const isSearching = isLoadingRuns && Boolean(contextSearch);

  useEffect(() => {
    if (term === contextSearch || !isCommittable(term)) return undefined;
    const timer = window.setTimeout(() => setContextSearch(term), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [term, contextSearch, setContextSearch]);

  const clear = () => {
    setValue('');
    setContextSearch('');
  };

  return (
    <Tooltip
      title={isTooShort ? `Digite ao menos ${MIN_LENGTH} caracteres` : ''}
      open={isTooShort}
      placement="bottom-start"
      arrow
    >
      <TextField
        size="small"
        value={value}
        placeholder="Buscar no contexto"
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && isCommittable(term)) setContextSearch(term);
          if (event.key === 'Escape' && value) clear();
        }}
        inputProps={{ 'aria-label': 'Buscar texto no contexto das execuções', maxLength: 200 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <ManageSearchRoundedIcon
                sx={{
                  fontSize: 18,
                  color: `${contextSearch ? colors.primary.yellow : colors.brand.textMuted} !important`,
                }}
              />
            </InputAdornment>
          ),
          endAdornment: isSearching ? (
            <InputAdornment position="end">
              <CircularProgress size={14} thickness={5} sx={{ color: colors.primary.yellow }} />
            </InputAdornment>
          ) : value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Limpar busca no contexto" onClick={clear} sx={{ padding: 0.25 }}>
                <CloseRoundedIcon sx={{ fontSize: 16, color: `${colors.brand.textSecondary} !important` }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            height: 36,
            paddingX: 1,
            fontSize: '13px',
            backgroundColor: colors.background.primary,
            borderRadius: '8px',
            '& fieldset': { borderColor: contextSearch ? 'rgba(255, 160, 21, 0.5)' : colors.brand.line },
            '&:hover fieldset': { borderColor: 'rgba(255, 160, 21, 0.5)' },
            '&.Mui-focused fieldset': { borderColor: colors.primary.yellow, borderWidth: '1px' },
            '& input': { color: colors.primary.white, fontFamily: monoFontFamily, fontSize: '12px' },
            '& input::placeholder': {
              color: colors.brand.textMuted,
              opacity: 1,
              fontFamily: 'inter, system-ui, sans-serif',
              fontSize: '13px',
            },
          },
        }}
      />
    </Tooltip>
  );
}
